import React from "react";
import {
  makeStyles,
  useTheme,
} from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import Grid from "@civicplus/preamble-ui/lib/Grid";
import { EventMeeting } from "../../types/types.event";
import EventVideoButton from "./EventVideoButton";
import SkeletonLoader from "@civicplus/preamble-ui/lib/SkeletonLoader";
import Button from "@civicplus/preamble-ui/lib/Button";
import AttachmentIcon from "@material-ui/icons/Attachment";
import MicIcon from "@material-ui/icons/Mic";
import { Bullhorn } from "mdi-material-ui";
import { MeetingFile } from "../../components/EventDetails/MeetingFiles/interfaces";
import EventDownloadFilesMenu from "./EventDownloadFilesMenu";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

const useStyles = makeStyles(theme => ({
  buttonGroup: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    boxShadow: theme.shadows[1],
    flexShrink: 0,
  },
  buttonContainer: {
    display: "flex",
    flex: "1 1 auto",
  },
  emptyButton: {
    width: theme.spacing(6),
    "@container eventListContainer (width < 400px)": {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
  },
  button: {
    padding: theme.spacing(0.25, 0.5),
    width: theme.spacing(8.75),
    textTransform: "none",
    lineHeight: theme.typography.body2.lineHeight,
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    "@container eventListContainer (width < 400px)": {
      padding: theme.spacing(0.5),
    },
  },
  legacyButton: {
    "@container eventListContainer (width < 400px)": {
      padding: theme.spacing(0.5),
    },
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: theme.spacing(8.75),
  },
  legacyLoading: {
    marginLeft: theme.spacing(0.25),
  },
  topSpacing: {
    marginTop: "0.3rem",
  },
}));

interface EventListRowSecondaryActionsProps extends Partial<EventMeeting> {
  loading?: boolean;
  id?: EventMeeting["id"];
}

export const EventListRowSecondaryActions: React.FC<
  EventListRowSecondaryActionsProps
> = props => {
  const {
    id,
    hasMedia,
    externalMediaUrl,
    publishedFiles,
    liveIsCurrentlyStreaming,
    loading,
    publicCommentsEnabled,
    cpMediaOnly,
    publishedAgendaTimeStamp,
  } = props;
  const classes = useStyles();
  const theme = useTheme();

  const portalCustomization = useSelector(
    (state: RootState) => state.portalCustomization
  );
  const shouldRender =
    hasMedia ||
    liveIsCurrentlyStreaming ||
    loading ||
    (publishedFiles && publishedFiles?.length > 0);

  const handlePublishedFiles = (publishedFiles: MeetingFile[]) => {
    const publishedFileList:
      | MeetingFile[]
      | {
          fileId: number;
          name: string;
          type: string;
          url: string | undefined;
          fileType: number;
          plainText?: boolean;
        }[] = [];
    publishedFiles.forEach(attachment => {
      publishedFileList.push({
        fileId: attachment.fileId,
        name: attachment.name,
        type: `${attachment.type} (PDF)`,
        url: attachment.url,
        fileType: attachment.fileType,
      });
      publishedFileList.push({
        fileId: attachment.fileId,
        name: attachment.name,
        type: `${attachment.type} (Plain Text)`,
        url: attachment.url,
        plainText: true,
        fileType: attachment.fileType,
      });
    });
    return (
      <Grid data-testid="files" className={classes.topSpacing}>
        <EventDownloadFilesMenu
          id={`downloadFilesMenu-${id}`}
          publishedFiles={publishedFileList}
          title="Download Files from this Event"
          cpMediaOnly={cpMediaOnly || false}
          className={classes.legacyButton}
          iconSize="medium"
        />
      </Grid>
    );
  };

  return portalCustomization?.enablePublicSpeakerSignup ? (
    <Grid className={classes.buttonGroup}>
      {!loading &&
        publicCommentsEnabled &&
        publishedFiles &&
        publishedFiles.some(
          file => file.type === "Agenda Packet" || file.type === "Agenda"
        ) && (
          <Grid
            className={classes.buttonContainer}
            data-testid="publicComments"
          >
            <Button
              variant="text"
              color="primary"
              className={classes.button}
              title="Go To Public Comment"
              TooltipProps={{ placement: "left" }}
              href={`/event/${id}/overview`}
              data-testid="Public Comment"
            >
              <MicIcon />
              Public Comment
            </Button>
          </Grid>
        )}
      {loading ? (
        <Grid className={classes.loading}>
          <SkeletonLoader
            circle={true}
            height={theme.spacing(4)}
            width={theme.spacing(4)}
          />
          <SkeletonLoader
            height={theme.spacing(1.8)}
            width={theme.spacing(8)}
          />
        </Grid>
      ) : (
        <Grid data-testid="video" className={classes.buttonContainer}>
          <EventVideoButton
            externalMediaUrl={externalMediaUrl}
            eventId={id ?? 1}
            isLive={liveIsCurrentlyStreaming}
            className={classes.button}
            disabled={!(hasMedia || liveIsCurrentlyStreaming)}
          />
        </Grid>
      )}
      {loading ? (
        <Grid className={classes.loading}>
          <SkeletonLoader
            circle={true}
            height={theme.spacing(4)}
            width={theme.spacing(4)}
          />
          <SkeletonLoader
            height={theme.spacing(1.8)}
            width={theme.spacing(8)}
          />
        </Grid>
      ) : (
        <Grid className={classes.buttonContainer}>
          <Button
            title={
              publishedFiles && publishedFiles?.length > 0
                ? undefined
                : "Go To Files"
            }
            TooltipProps={{ placement: "left" }}
            variant="text"
            color="primary"
            className={classes.button}
            href={`event/${id}/files`}
            disabled={!(publishedFiles && publishedFiles?.length > 0)}
            data-testid="files"
          >
            <AttachmentIcon fontSize="medium" />
            Files
          </Button>
        </Grid>
      )}
    </Grid>
  ) : shouldRender ? (
    <>
      {loading ? (
        <Grid>
          <SkeletonLoader
            circle={true}
            height={theme.spacing(5)}
            width={theme.spacing(5)}
          />
        </Grid>
      ) : (
        (liveIsCurrentlyStreaming || loading || hasMedia || externalMediaUrl) &&
        id && (
          <Grid data-testid="video">
            <EventVideoButton
              externalMediaUrl={externalMediaUrl}
              eventId={id}
              isLive={liveIsCurrentlyStreaming}
              className={classes.legacyButton}
            />
          </Grid>
        )
      )}
      {loading ? (
        <Grid>
          <SkeletonLoader
            circle={true}
            height={theme.spacing(5)}
            width={theme.spacing(5)}
          />
        </Grid>
      ) : (
        publishedAgendaTimeStamp && (
          <Grid data-testid="publishedTimeStamp">
            <Button
              type="icon"
              stopPropagation={true}
              title={publishedAgendaTimeStamp}
              color="primary"
              className={classes.legacyButton}
            >
              <Bullhorn />
            </Button>
          </Grid>
        )
      )}
      {loading ? (
        <Grid>
          <SkeletonLoader
            circle={true}
            height={theme.spacing(5)}
            width={theme.spacing(5)}
            className={classes.legacyLoading}
          />
        </Grid>
      ) : publishedFiles && publishedFiles?.length > 0 && id ? (
        handlePublishedFiles(publishedFiles)
      ) : (
        <Grid className={classes.emptyButton}></Grid>
      )}
    </>
  ) : (
    <></>
  );
};

export default EventListRowSecondaryActions;
